import * as d3 from 'd3'
import _ from 'underscore'

import { timeScaleWeighted } from '../helper/d3.weightedTimeScale'

let Chart = function() {
    var svg = null

    var dimensions = {
        width: 0,
        height: 0,
    }

    var margin = {
        top: 5,
        right: 20,
        bottom: 15,
        left: 40,
    }

    var infoBox = {
        width: 144,
        height: 55,
    }

    var tickFormatter = null
    var tooltipMessage = null
    var tooltipValueLabel = 'Count'
    var barGap = 4

    this.create = function(rootElement, width, height, tickFormatterCallBack, tooltipValueLabelText = 'Count') {
        svg = rootElement
        dimensions.width = width
        dimensions.height = height

        tickFormatter = tickFormatterCallBack
        tooltipValueLabel = tooltipValueLabelText
        let d3Svg = d3
            .select(svg)
            .attr('class', 'chart')
            .attr('width', dimensions.width)
            .attr('height', dimensions.height)

        d3Svg
            .append('defs')
            .append('clipPath')
            .attr('id', 'histogram-clip')
            .append('rect')
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)

        let container = d3Svg.append('g').attr('class', 'container')

        let axis = container.append('g').attr('class', 'axis')

        axis.append('g').attr('class', 'y')
        axis.append('g').attr('class', 'x')

        let innerGraph = d3Svg
            .append('g')
            .attr('class', 'innerGraph')
            .attr('clip-path', 'url(#histogram-clip)')
            .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

        innerGraph.append('g').attr('class', 'lines')

        innerGraph.append('g').attr('class', 'rangeMarkers')

        _drawTooltip(innerGraph)
    }

    var _scales = function(lines) {
        return {
            x: d3
                .scaleTime()
                .domain([_.min(_.pluck(lines[0], 'x')), _.max(_.pluck(lines[0], 'x'))])
                .range([0, dimensions.width - margin.left - margin.right]),
            y: d3
                .scaleLinear()
                .domain([Math.max(_.max(_.pluck(lines[0], 'y')), 1), 0])
                .range([0, dimensions.height - margin.top - margin.bottom]),
        }
    }

    var _getTickFormatter = function(d) {
        return tickFormatter ? tickFormatter(d) : (d / 1000).toFixed() + 'ms'
    }

    var _axis = function(scales) {
        return {
            x: d3
                .axisBottom(scales.x)
                .tickSizeInner(-(dimensions.height - margin.top - margin.bottom))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(10)
                .tickFormat(_getTickFormatter),
            y: d3
                .axisLeft(scales.y)
                .tickSizeInner(-(dimensions.width - margin.left - margin.right))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(5),
        }
    }

    var _drawAxis = function(scales, axis) {
        var root = d3.select(svg).select('.axis')

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        root.selectAll('.x')
            .call(axis.x)
            .attr('transform', 'translate(0, ' + (dimensions.height - margin.bottom - margin.top) + ')')
        root.selectAll('.y').call(axis.y)

        root.selectAll('.axis .x text').attr('class', d => timeScaleWeighted(d, axis.x.scale().domain()))
    }

    var _getTooltipMessage = function(x, end) {
        return tooltipMessage ? tooltipMessage(x, end) : [_getTickFormatter(x) + ' - ' + _getTickFormatter(end), '']
    }

    var _getBarRangeType = function(xValue, rangeMarkers) {
        for (let i = rangeMarkers.length - 1; i >= 0; i--) {
            let marker = rangeMarkers[i]
            if (marker.type === 'timeExceeded') {
                continue
            }
            if (xValue >= marker.start) {
                return marker.type
            }
        }
        return 'success'
    }

    var _drawData = function(scales, lines, rangeMarkers) {
        let root = d3.select(svg).selectAll('.lines')
        let tooltip = d3.select(svg).select('.infoBox')
        let bisect = d3.bisector(function(d) {
            return d.x
        }).right
        // @todo duplicate calculation, could be taken from clip-path
        const width = dimensions.width - margin.left - margin.right
        const height = dimensions.height - margin.top - margin.bottom

        d3.select(svg)
            .on('mouseover', function() {
                tooltip.style('display', null)
            })
            .on('mouseout', function() {
                tooltip.style('display', 'none')
            })
            .on('mousemove', function() {
                let x0 = scales.x.invert(d3.mouse(this)[0] - margin.left)
                let dataSet = lines[0][bisect(lines[0], x0) - 1]

                if (!dataSet) {
                    return
                }

                let position = {
                    x: d3.mouse(this)[0] + 10 - margin.left,
                    y: d3.mouse(this)[1] + 5 - margin.top,
                }

                if (position.x + infoBox.width > width) {
                    position.x = position.x - 20 - infoBox.width
                }

                if (position.y + infoBox.height > height) {
                    position.y = position.y - 10 - infoBox.height
                }

                tooltip.attr('transform', 'translate(' + position.x + ', ' + position.y + ')')
                let tooltipText = _getTooltipMessage(dataSet.x, dataSet.end)
                tooltip.select('.time').text(tooltipText[0])
                tooltip.select('.timeExceeded').text(tooltipText[1])
                tooltip.select('.count').text(dataSet.y || '-')
            })

        let hasColoredBars = rangeMarkers && rangeMarkers.length > 0

        if (hasColoredBars) {
            _drawColoredBars(root, scales, lines, rangeMarkers, height)
        } else {
            _drawAreaFill(root, scales, lines)
        }
    }

    var _drawColoredBars = function(root, scales, lines, rangeMarkers, chartHeight) {
        root.selectAll('g.line').remove()

        let data = lines[0].filter(function(d) {
            return d.y > 0
        })

        let bars = root.selectAll('rect.bar').data(data, function(d) {
            return d.x.getTime()
        })

        let getBarX = function(d) {
            return scales.x(d.x) + barGap / 2
        }

        let getBarWidth = function(d) {
            return Math.max(scales.x(d.end) - scales.x(d.x) - barGap, 1)
        }

        bars.enter()
            .append('rect')
            .attr('class', function(d) {
                return 'bar bar-' + _getBarRangeType(d.end.getTime(), rangeMarkers)
            })
            .attr('x', getBarX)
            .attr('width', getBarWidth)
            .attr('y', function(d) {
                return scales.y(d.y)
            })
            .attr('height', function(d) {
                return chartHeight - scales.y(d.y)
            })

        bars.attr('class', function(d) {
            return 'bar bar-' + _getBarRangeType(d.end.getTime(), rangeMarkers)
        })
            .transition()
            .attr('x', getBarX)
            .attr('width', getBarWidth)
            .attr('y', function(d) {
                return scales.y(d.y)
            })
            .attr('height', function(d) {
                return chartHeight - scales.y(d.y)
            })

        bars.exit().remove()
    }

    var _drawAreaFill = function(root, scales, lines) {
        root.selectAll('rect.bar').remove()

        let pathFunction = d3
            .line()
            .x(function(point) {
                return scales.x(point.x)
            })
            .y(function(point) {
                return scales.y(point.y)
            })
            .curve(d3.curveStepAfter)
        let areaFunction = d3
            .area()
            .x(function(point) {
                return scales.x(point.x)
            })
            .y0(dimensions.height - margin.top - margin.bottom)
            .y1(function(point) {
                return scales.y(point.y)
            })
            .curve(d3.curveStepAfter)

        let line = root.selectAll('g.line').data(lines)

        // New data
        let newLine = line.enter()
        let group = newLine.append('g').attr('class', 'line')

        group
            .append('path')
            .attr('class', 'line')
            .attr('d', function(line) {
                return pathFunction(line)
            })
        group
            .append('path')
            .attr('class', 'fill')
            .attr('d', function(line) {
                return areaFunction(line)
            })

        // Update existing nodes
        line.select('path.line')
            .attr('d', function(line) {
                return pathFunction(line)
            })
            .attr('transform', null)
            .transition()
            .attr('transition', 'translate(' + scales.x(-1) + ')')
        line.select('path.fill')
            .attr('d', function(line) {
                return areaFunction(line)
            })
            .attr('transform', null)
            .transition()
            .attr('transition', 'translate(' + scales.x(-1) + ')')

        // Exiting node
        line.exit().remove()
    }

    var _drawRangeMarkers = function(scales, rangeMarkers) {
        if (typeof rangeMarkers === 'undefined') {
            return
        }

        var root = d3.select(svg).select('.rangeMarkers')

        let marker = root.selectAll('g.rangeMarker').data(rangeMarkers)

        // New data
        let newevent = marker.enter()
        let group = newevent.append('g').attr('class', function(marker) {
            return 'rangeMarker ' + marker.type
        })

        group
            .append('rect')
            .attr('y', 0)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('x', function(marker) {
                return scales.x(marker.start)
            })
            .attr('width', function(marker) {
                return scales.x(marker.end) - scales.x(marker.start)
            })

        // Update existing nodes
        marker
            .select('rect')
            .transition()
            .attr('x', function(marker) {
                return scales.x(marker.start) - 5
            })
            .attr('width', function(marker) {
                return scales.x(marker.end) - scales.x(marker.start)
            })

        // Exiting node
        marker.exit().remove()
    }

    var _drawTooltip = function(container) {
        let tooltip = container
            .append('g')
            .attr('class', 'infoBox')
            .style('display', 'none')

        let rowHeight = (infoBox.height - 10) / 2

        tooltip
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', infoBox.width)
            .attr('height', infoBox.height)

        tooltip
            .append('text')
            .attr('class', 'time')
            .attr('x', 5)
            .attr('y', 5 + 14)
            .text('')

        tooltip
            .append('text')
            .attr('class', 'timeExceeded')
            .attr('x', infoBox.width - 35)
            .attr('y', 5 + 14)
            .text('')

        tooltip
            .append('text')
            .attr('class', 'label count-label')
            .attr('x', 5)
            .attr('y', 5 + rowHeight * 2)
            .text(tooltipValueLabel)

        tooltip
            .append('text')
            .attr('class', 'value count')
            .attr('x', infoBox.width - 5)
            .attr('y', 5 + rowHeight * 2)
            .text('')
    }

    var _appendValue = function(data) {
        let last = data[data.length - 1]

        if (!last) {
            return
        }

        let next = _.clone(last)

        next.x = next.end
        next.y = 0 // Not necessary, but the chart closes in a more sensible way

        data[data.length] = next
        return data
    }

    this.update = function(lines, markers, tickFormatterCallBack, tooltipMessageCallBack) {
        if (!Array.isArray(lines) || lines.length === 0) {
            return
        }
        tickFormatter = tickFormatterCallBack
        tooltipMessage = tooltipMessageCallBack
        lines = _.map(lines, _appendValue)

        let scales = _scales(lines)

        _drawAxis(scales, _axis(scales))
        _drawData(scales, lines, markers || [])
        _drawRangeMarkers(scales, [])
    }

    this.destroy = function() {
        // remove all children from the svg.
        d3.select(svg)
            .selectAll('*')
            .remove()
    }
}

export default Chart
